import { ArrowRight, MailCheck, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { trackCTAClick } from "@/lib/analytics";

export const Hero = () => (
    <section id="top" className="relative overflow-hidden bg-[#F8FAFC]">
        <div
            className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#60A5FA]/10 blur-3xl"
            aria-hidden="true"
        />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-20 pt-32 lg:min-h-[85vh] lg:grid-cols-2 lg:px-8">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
            >
                <p
                    data-testid="hero-eyebrow"
                    className="mb-5 inline-flex items-center rounded-full border border-[#2563EB]/20 bg-[#2563EB]/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#2563EB]"
                >
                    AI Growth System for UK Service Businesses
                </p>
                <h1
                    data-testid="hero-headline"
                    className="text-4xl font-bold leading-[1.08] tracking-tight text-[#0B1F44] sm:text-5xl lg:text-6xl"
                >
                    Turn marketing activity into{" "}
                    <span className="text-[#2563EB]">qualified enquiries</span>{" "}
                    and revenue.
                </h1>
                <p
                    data-testid="hero-subheadline"
                    className="mt-6 max-w-xl text-base leading-relaxed text-[#4B5563] lg:text-lg"
                >
                    FlowPilot combines AI, advertising intelligence, lead
                    qualification, follow-up and revenue tracking so businesses
                    can see what is working and where to reinvest.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <a
                        href="#growth-review"
                        data-testid="hero-primary-cta"
                        onClick={() => trackCTAClick("hero")}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/20"
                    >
                        Book a 20-minute Growth Review
                        <ArrowRight className="h-4 w-4" />
                    </a>
                    <a
                        href="#how-it-works"
                        data-testid="hero-secondary-cta"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0B1F44] bg-white px-7 py-3.5 text-base font-semibold text-[#0B1F44] transition-colors hover:bg-gray-50"
                    >
                        See How FlowPilot Works
                        <ArrowRight className="h-4 w-4" />
                    </a>
                </div>
                <p className="mt-6 text-sm text-[#6B7280]">
                    Currently being tested with UK service businesses.
                </p>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="relative"
            >
                <div className="fp-card-shadow relative overflow-hidden rounded-3xl border border-gray-100">
                    <img
                        src="https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdWUlMjB3YXZlfGVufDB8fHx8MTc5MTczNTA3Nnww&ixlib=rb-4.1.0&q=85&w=1200"
                        alt="Abstract blue flow visual representing marketing activity turning into revenue"
                        className="h-[320px] w-full object-cover sm:h-[420px]"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F44]/40 via-transparent to-transparent" />
                </div>
                <div
                    data-testid="hero-chip-enquiry"
                    className="fp-card-shadow absolute -left-3 top-8 flex items-center gap-3 rounded-2xl border border-gray-100 bg-white/95 px-4 py-3 backdrop-blur sm:-left-6"
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2563EB]/10">
                        <MailCheck className="h-4 w-4 text-[#2563EB]" />
                    </span>
                    <div>
                        <p className="text-xs font-semibold text-[#0B1F44]">
                            New qualified enquiry
                        </p>
                        <p className="text-xs text-[#6B7280]">
                            Followed up within minutes
                        </p>
                    </div>
                </div>
                <div
                    data-testid="hero-chip-revenue"
                    className="fp-card-shadow absolute -bottom-5 right-4 flex items-center gap-3 rounded-2xl border border-gray-100 bg-white/95 px-4 py-3 backdrop-blur sm:right-8"
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2563EB]/10">
                        <TrendingUp className="h-4 w-4 text-[#2563EB]" />
                    </span>
                    <div>
                        <p className="text-xs font-semibold text-[#0B1F44]">
                            Revenue attributed
                        </p>
                        <p className="text-xs text-[#6B7280]">
                            Google Ads &rarr; Sale
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);
